import React from 'react'
import { Link } from '@tanstack/react-router'
import { useTar } from '@/modules/i18n/useTar'
import { Logo } from '../Misc/Logo'
import { ExternalURLs } from '@/types/externalURLs'
import { LinkedInIcon, DiscordIcon } from '@duck/ui'

export function Footer() {
    const { t } = useTar()
    const currentYear = new Date().getFullYear()

    return (
        <footer className="w-full border-t border-border bg-card/50 backdrop-blur-sm mt-auto">
            <div className="max-w-6xl mx-auto px-8 py-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 text-center md:text-start">
                    {/* Brand Section */}
                    <div className="space-y-4 flex flex-col items-center md:items-start">
                        <Link to="/" className="flex items-center gap-2 w-fit">
                            <Logo className="text-2xl" />
                        </Link>
                    </div>

                    {/* Challenges Section */}
                    <div className="space-y-4 flex flex-col items-center md:items-start">
                        <h3 className="font-semibold text-foreground">{t('navigation.challenges')}</h3>
                        <ul className="space-y-3 flex flex-col items-center md:items-start">
                            <li>
                                <Link
                                    to="/challenges/available"
                                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                                >
                                    {t('challenges.availableChallenges')}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/challenges/completed"
                                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                                >
                                    {t('navigation.completedChallenges')}
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* About Section */}
                    <div className="space-y-4 flex flex-col items-center md:items-start">
                        <h3 className="font-semibold text-foreground">{t('navigation.about')}</h3>
                        <ul className="space-y-3 flex flex-col items-center md:items-start">
                            <li>
                                <Link
                                    to="/about"
                                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                                >
                                    {t('navigation.aboutUs')}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/faq"
                                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                                >
                                    {t('navigation.faq')}
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Connect Section */}
                    <div className="space-y-4 flex flex-col items-center md:items-start">
                        <h3 className="font-semibold text-foreground">{t('navigation.connect')}</h3>
                        <div className="flex gap-4 justify-center md:justify-start">
                            {/* Social Media Icons - Using simple SVG icons */}
                            <a
                                href={ExternalURLs.DiscordInvite}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-muted-foreground hover:text-primary transition-colors"
                                aria-label="Discord"
                            >
                                <DiscordIcon size={18} />
                            </a>
                            <a
                                href={ExternalURLs.LinkedInPage}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-muted-foreground hover:text-primary transition-colors"
                                aria-label="LinkedIn"
                            >
                                <LinkedInIcon size={18} />
                            </a>
                        </div>
                        <div className="flex gap-1 justify-center md:justify-start">
                            <span className="text-sm text-muted-foreground">{t('common.foundABug')}</span>
                            <a
                                href={ExternalURLs.DiscordInviteIntoBugReportChannel}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-muted-foreground underline hover:text-primary transition-colors"
                            >
                                {t('common.letUsKnow')}
                            </a>
                        </div>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="mt-12 pt-8 border-t border-border">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <p className="text-sm text-muted-foreground text-center md:text-start">
                            © {currentYear} {t('common.appName')}. {t('common.allRightsReserved')}.
                        </p>
                        <div className="flex gap-6 justify-center md:justify-start">
                            <Link
                                to="/privacy-policy"
                                className="text-sm text-muted-foreground hover:text-primary transition-colors"
                            >
                                {t('common.privacyPolicy')}
                            </Link>
                            <Link
                                to="/terms-of-service"
                                className="text-sm text-muted-foreground hover:text-primary transition-colors"
                            >
                                {t('common.termsOfService')}
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    )
}

